import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/lib/currencyFormatter";
import { CartItem } from "./CartItem";
import { EmptyCart } from "./EmptyCart";
import { useTranslation } from "react-i18next";
import { CartItem as CartItemType } from "@/types/orders";
import { CreditCard, Banknote, AlertCircle } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { initiatePayment } from "@/services/payments/paymentService";
import { TipSelector } from "./TipSelector";

interface CartContentProps {
  cartItems: CartItemType[];
  specialInstructions: string;
  setSpecialInstructions: (value: string) => void;
  selectedTip: number;
  setSelectedTip: (percentage: number) => void;
  updateQuantity: (id: string, delta: number) => void;
  removeItem: (id: string) => void;
  clearCart: () => void;
  handleSubmitOrder: (paymentMethod: "Cash" | "Digital" | null) => void;
  isSubmitting: boolean;
  setIsCartOpen: (open: boolean) => void;
  storeId: string;
  tableId: string;
  calculateSubtotal: () => number;
}

export function CartContent({
  cartItems,
  specialInstructions,
  setSpecialInstructions,
  selectedTip,
  setSelectedTip,
  updateQuantity,
  removeItem,
  clearCart,
  handleSubmitOrder,
  isSubmitting,
  setIsCartOpen,
  storeId,
  tableId,
  calculateSubtotal,
}: CartContentProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [paymentMethod, setPaymentMethod] = useState<"Cash" | "Digital" | null>(
    null
  );
  const subtotal = calculateSubtotal();
  const tipAmount = (subtotal * selectedTip) / 100;
  const total = subtotal + tipAmount;

  // const handleOrderSubmit = async () => {
  //   if (!paymentMethod) return;

  //   if (paymentMethod === "Digital") {
  //     try {
  //       // Initialize payment with Paystack
  //       const paymentResult = await initiatePayment(
  //         crypto.randomUUID(), // temporary order ID
  //         storeId,
  //         calculateTotal(),
  //         "customer@example.com", // TODO: Get customer email
  //         tableId
  //       );

  //       if (!paymentResult.success) {
  //         toast({
  //           title: t("error"),
  //           description: paymentResult.error || t("paymentFailed"),
  //           variant: "destructive",
  //         });
  //         return;
  //       }

  //       // If payment initialization was successful, redirect to Paystack
  //       if (paymentResult.checkoutUrl) {
  //         window.location.href = paymentResult.checkoutUrl;
  //       }
  //     } catch (error) {
  //       console.error("Payment initialization error:", error);
  //       toast({
  //         title: t("error"),
  //         description: t("paymentFailed"),
  //         variant: "destructive",
  //       });
  //     }
  //   } else {
  //     // For cash payments, proceed with normal order submission
  //     await handleSubmitOrder(paymentMethod);
  //   }
  // };

  if (cartItems.length === 0) {
    return <EmptyCart onClose={() => setIsCartOpen(false)} />;
  }

  return (
    <>
      <div className="space-y-4 max-h-[60vh] overflow-y-auto py-2 overflow-x-hidden">
        {cartItems.map((item) => (
          <Card key={item.id} className="overflow-hidden">
            <CartItem
              id={item.id}
              menuItemName={item.menuItemName}
              price={item.price}
              quantity={item.quantity}
              imageUrl={item.imageUrl}
              updateQuantity={updateQuantity}
              removeItem={removeItem}
            />
          </Card>
        ))}

        <div className="pt-4">
          <label className="text-sm font-medium mb-1 block">
            {t("specialInstructionsLabel")}
          </label>
          <Textarea
            placeholder={t("specialInstructionsPlaceholder")}
            value={specialInstructions}
            onChange={(e) => setSpecialInstructions(e.target.value)}
            rows={3}
          />
        </div>

        {/* Tip Selector */}
        <div className="pt-4">
          <TipSelector
            selectedTip={selectedTip}
            onTipChange={setSelectedTip}
            subtotal={subtotal}
          />
        </div>

        <div className="space-y-4">
          <Label>{t("selectPaymentMethod")}</Label>
          <RadioGroup
            value={paymentMethod || ""}
            onValueChange={(value) =>
              setPaymentMethod(value as "Cash" | "Digital")
            }
            className="grid grid-cols-2 gap-4"
          >
            <div>
              <RadioGroupItem
                value="Digital"
                id="digital"
                className="peer sr-only"
              />
              <Label
                htmlFor="digital"
                className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-transparent p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
              >
                <CreditCard className="mb-2 h-6 w-6" />
                {t("digitalPayment")}
              </Label>
            </div>

            <div>
              <RadioGroupItem value="Cash" id="cash" className="peer sr-only" />
              <Label
                htmlFor="cash"
                className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-transparent p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
              >
                <Banknote className="mb-2 h-6 w-6" />
                {t("cashPayment")}
              </Label>
            </div>
          </RadioGroup>
        </div>
      </div>

      <div className="flex-col gap-3 mt-4">
        <div className="space-y-2 pt-2 border-t">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">{t("subtotalLabel", "Subtotal")}</span>
            <span className="text-sm">{formatCurrency(subtotal)}</span>
          </div>
          {selectedTip > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">{t("tipLabel", "Tip")} ({selectedTip}%)</span>
              <span className="text-sm text-green-600">+{formatCurrency(tipAmount)}</span>
            </div>
          )}
          <div className="flex justify-between items-center pt-2 border-t">
            <span className="font-medium text-lg">{t("totalLabel")}</span>
            <span className="font-bold text-xl">
              {formatCurrency(total)}
            </span>
          </div>
        </div>

        <div className="flex gap-2 w-full mt-4">
          <Button variant="outline" className="flex-1" onClick={clearCart}>
            {t("clearCartButton")}
          </Button>
          <Button
            className="flex-1"
            onClick={() => handleSubmitOrder(paymentMethod)}
            disabled={isSubmitting || cartItems.length === 0 || !paymentMethod}
          >
            {isSubmitting ? t("processing") : t("placeOrderButton")}
          </Button>
        </div>
      </div>
    </>
  );
}
